import { datadogLogs } from '@datadog/browser-logs'
import { filter, groupBy } from 'lodash'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'

import employees from './employees'

const employeeContractVersions = (employeeId: number, contractVersions: ContractVersions) => {
  return contractVersions.filter({
    employee_id: employeeId,
  })
}

/**
 * Filter employees that are active and
 * they have at least one contract version
 *
 */
const employeesWithContract = (contractVersions: ContractVersions) => {
  return employees.active.filter(
    (employee) => !!employeeContractVersions(employee.id, contractVersions).length
  )
}
export const isOrphan = (employee: Employee) => {
  if (!employee.attributes.manager_id) {
    return false
  }

  const paths = tracePath(employee.attributes.manager_id, employee)
  const parentId = paths[paths.length - 1]

  const manager = employees.get(parentId)

  return !manager
}

const tracePath = (manager_id: number, employee: Employee): number[] => {
  const idTreePath: number[] = []
  const visitedManagerIds = new Set<number>()

  while (manager_id) {
    if (visitedManagerIds.has(manager_id)) {
      const data = {
        message: 'Infinite loop detected in tracePath',
        employee_id: employee.attributes.id,
        manager_id,
      }

      datadogLogs.logger.log('Circular reference', data)
      break
    }
    visitedManagerIds.add(manager_id)

    const manager = employees.get(manager_id)

    if (!manager?.attributes) break

    idTreePath.push(manager.attributes.id)
    manager_id = manager.attributes.manager_id ?? 0
  }
  return idTreePath
}

export const peopleHierarchicallyManagedByEmployee = (
  employee: Employee,
  contractVersions: ContractVersions
) => {
  return employeesWithContract(contractVersions).filter((employeeWithContract) => {
    const idPath = tracePath(employeeWithContract.attributes.manager_id ?? 0, employee)
    const lastIdInPath = idPath[idPath.length - 1]

    return employee.attributes.id === lastIdInPath
  })
}
export const employeeDirectlyManagedEmployees = (
  employee: Employee,
  contractVersions: ContractVersions
) => {
  return filter(
    employees.filter({
      manager_id: employee.attributes.id,
    }),
    (managedEmployee) =>
      !managedEmployee.isTerminated &&
      !!employeeContractVersions(managedEmployee.id, contractVersions).length
  )
}

/**
 * Filter users that are not:
 *
 *   - Yourself
 *   - Terminated
 *   - Any of your descendants
 */
export const employeesThatCanBeMyManager = (
  employee: Employee,
  contractVersions: ContractVersions
) => {
  const managed = employeeManagedEmployees(employee, contractVersions)
  const descendants = groupBy(managed, (e) => e.attributes.id)

  return employees.filter((model) => {
    if (model.isTerminated) return false

    if (descendants[model.attributes.id]) return false

    if (model.attributes.id !== employee.attributes.id) return true
  })
}

export const employeeManagedEmployees = (
  employee: Employee,
  contractVersions: ContractVersions
): Employee[] => {
  const queue: Employee[] = [employee]
  const allManagedEmployees: Employee[] = []
  const visitedEmployeeIds = new Set<number>()

  while (queue.length > 0) {
    const currentEmployee = queue.shift()
    if (!currentEmployee) continue

    const { id: employeeID } = currentEmployee.attributes
    if (visitedEmployeeIds.has(employeeID)) continue
    visitedEmployeeIds.add(employeeID)

    const directManagedEmployees = filter(
      employees.filter({
        manager_id: employeeID,
      }),
      (managedEmployee) =>
        !managedEmployee.isTerminated &&
        employeeContractVersions(managedEmployee.id, contractVersions).length > 0
    )

    allManagedEmployees.push(...directManagedEmployees)
    queue.push(...directManagedEmployees)
  }
  return allManagedEmployees
}
